import React, { useEffect, useState } from "react";
import { X, Send, Mail, AlertCircle, Megaphone, MessageSquareText, RefreshCw, Check } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const TEMPLATES = [
  {
    id: "campaign_created",
    title: "New campaign created",
    desc: "Heads-up that a new campaign has been added for the brand.",
    icon: Megaphone,
  },
  {
    id: "new_comment",
    title: "New comment(s) on campaign",
    desc: "Highlight one or more comments from the thread.",
    icon: MessageSquareText,
  },
  {
    id: "status_updated",
    title: "Campaign status updated",
    desc: "Standard alert using the campaign's current status.",
    icon: RefreshCw,
  },
];

function shortTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function NotifyBrandModal({ campaign, onClose }) {
  const [templateId, setTemplateId] = useState("campaign_created");
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/notify/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  // Lazy-load comments only when needed.
  useEffect(() => {
    if (templateId !== "new_comment") return;
    setLoadingComments(true);
    api.get(`/campaigns/${campaign.id}/comments`)
      .then((r) => {
        const live = (r.data.comments || []).filter((c) => !c.deleted);
        setComments(live);
        // Default-select the most recent comment.
        if (live.length > 0) setSelectedIds([live[0].id]);
      })
      .catch(() => toast.error("Couldn't load comments."))
      .finally(() => setLoadingComments(false));
  }, [templateId, campaign.id]);

  const tpl = TEMPLATES.find((t) => t.id === templateId);

  const toggle = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const submit = async () => {
    if (templateId === "new_comment") {
      if (comments.length === 0) {
        toast.error("This campaign has no comments yet — post one first.");
        return;
      }
      if (selectedIds.length === 0) {
        toast.error("Pick at least one comment to highlight.");
        return;
      }
    }
    setSending(true);
    try {
      const payload = { campaign_id: campaign.id, template: templateId };
      if (templateId === "new_comment") payload.comment_ids = selectedIds;
      const res = await api.post("/notify/campaign", payload);
      const { sent, failed, recipients, configured } = res.data;
      if (!configured) {
        toast.success(`Email queued in stub mode (BREVO_API_KEY not set). ${recipients.length} recipient(s).`);
      } else if (failed > 0) {
        toast.warning(`Sent ${sent}/${sent + failed}. Some recipients failed.`);
      } else {
        toast.success(`Email sent to ${sent} recipient(s).`);
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send notification.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="notify-brand-modal">
      <div className="wl-card-solid w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-[var(--brand)]" />
            <h2 className="text-base font-semibold">Notify brand — {campaign.campaign_name}</h2>
          </div>
          <button onClick={onClose} className="wl-btn-ghost p-1" data-testid="notify-close"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          {status && !status.configured && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs">
              <AlertCircle size={14} className="mt-0.5" />
              <span>Email provider not configured — the email will be logged but not delivered. Add <code className="font-mono">BREVO_API_KEY</code> to enable real sending.</span>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Pick a template</label>
            <div className="mt-2 space-y-2">
              {TEMPLATES.map((t) => {
                const Icon = t.icon;
                const active = templateId === t.id;
                return (
                  <label key={t.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${active ? "border-[var(--brand)] bg-[var(--brand-tint-soft)]" : "border-[var(--border-default)] hover:bg-[var(--bg-page)]"}`}>
                    <input type="radio" className="mt-1 accent-[var(--brand)]" name="tpl" value={t.id} checked={active} onChange={() => setTemplateId(t.id)} data-testid={`notify-tpl-${t.id}`} />
                    <Icon size={16} className={`mt-0.5 ${active ? "text-[var(--brand)]" : "text-[var(--text-secondary)]"}`} />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{t.title}</div>
                      <div className="text-xs text-[var(--text-secondary)]">{t.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {templateId === "new_comment" && (
            <div data-testid="notify-comment-picker">
              <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide flex items-center justify-between">
                <span>Comments to highlight</span>
                <span className="text-[10px] tracking-normal normal-case text-[var(--text-secondary)]">{selectedIds.length} selected</span>
              </label>
              <div className="mt-2 border border-[var(--border-default)] rounded-lg divide-y divide-[var(--border-default)] max-h-60 overflow-y-auto bg-[var(--bg-surface)]">
                {loadingComments && <div className="p-4 text-xs text-[var(--text-secondary)]">Loading comments…</div>}
                {!loadingComments && comments.length === 0 && (
                  <div className="p-4 text-xs text-[var(--text-secondary)]">No comments yet on this campaign. Post one in the Comments drawer before sending this email.</div>
                )}
                {!loadingComments && comments.map((c) => {
                  const checked = selectedIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggle(c.id)}
                      className={`w-full text-left p-3 flex items-start gap-3 transition ${checked ? "bg-[var(--brand-tint-soft)]" : "hover:bg-[var(--bg-page)]"}`}
                      data-testid={`notify-comment-pick-${c.id}`}
                    >
                      <span className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded border ${checked ? "bg-[var(--brand)] border-[var(--brand)] text-white" : "border-[var(--border-default)] bg-white"}`}>
                        {checked && <Check size={11} strokeWidth={3} />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-[var(--text-primary)] truncate">
                          {c.user_email} <span className="text-[var(--text-secondary)] font-normal">· {shortTime(c.created_at)}</span>
                        </div>
                        <div className="text-xs text-[var(--text-secondary)] mt-0.5 line-clamp-3 whitespace-pre-wrap break-words">{c.comment_text}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-1.5 text-[11px] text-[var(--text-secondary)]">Tick one or more comments to include in the email. They'll be quoted in the order they were posted.</div>
            </div>
          )}

          {templateId === "status_updated" && (
            <div className="px-3 py-2.5 rounded-md bg-[var(--bg-surface-warm)] border border-[var(--border-default)] text-xs text-[var(--text-primary)]">
              The email will reference the campaign's current status: <strong>{campaign.status || "—"}</strong>.
            </div>
          )}

          <div className="text-xs text-[var(--text-secondary)] pt-2 border-t border-[var(--border-default)]">
            Recipients: <strong className="text-[var(--text-primary)]">all active brand users on this campaign's teams</strong>. Sender: {status?.sender_name} &lt;{status?.sender_email}&gt;.
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[var(--border-default)] flex justify-end gap-2">
          <button onClick={onClose} className="wl-btn-ghost text-sm" data-testid="notify-cancel">Cancel</button>
          <button onClick={submit} disabled={sending} className="wl-btn-primary text-sm inline-flex items-center gap-1.5" data-testid="notify-send">
            <Send size={14} /> {sending ? "Sending…" : "Send notification"}
          </button>
        </div>
      </div>
    </div>
  );
}
